import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import { secureGet, securePut } from '../../config/api';
import LoggedUserCard from '../../components/LoggedUserCard';
import SnackbarNotification from '../../components/SnackbarNotification';
import LoadingLogo from '../../components/LoadingLogo';
import MaintenanceAuthPopup from './MaintenanceAuthPopup';
import TaskDetailPopup from './TaskDetailPopup';
import logoGF from '../../images/logo2.png';
import wrenchIcon from '../../icons/broom.svg';
import LoaderIcon from '../../icons/loader.svg';

const blurStyle = {
  position: 'absolute',
  width: '100%',
  height: '150px',
  background: '#3872B0',
  opacity: 0.15,
  filter: 'blur(50px)',
  zIndex: 0,
};

// FAMILIES will be fetched from API

const TECHNICIAN_COLORS = ['#043F88', '#06D6A0', '#FEA26F', '#9B51E0', '#07645B', '#973B9D', '#D6C69F', ];

const STATUSES = [
  { key: 'TODO', label: 'À Faire' },
  { key: 'ONGOING', label: 'En Cours' },
  { key: 'PAUSED', label: 'En Pause' },
  { key: 'BLOCKED', label: 'Bloqué' },
  { key: 'DONE', label: 'Terminé' },
];

const Kanban = () => {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState(null); // No tab selected by default
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [families, setFamilies] = useState([]);
  const [vehicles, setVehicles] = useState({}); // Map of vehicleId -> vehicle data
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showMaintenancePopup, setShowMaintenancePopup] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [reloadCountdown, setReloadCountdown] = useState(120); // 2 minutes in seconds
  const selectedRoleRef = useRef(selectedRole);
  const familiesRef = useRef(families);
  
  // Keep refs updated with latest values
  useEffect(() => {
    selectedRoleRef.current = selectedRole;
    familiesRef.current = families;
  }, [selectedRole, families]);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Format duration from hours (decimal) to "XhYm" format
  const formatDuration = (hours) => {
    // Handle null, undefined, or 0
    if (!hours || hours === 0) return '0h';
    
    // Convert to number if it's a string
    const hoursNum = typeof hours === 'string' ? parseFloat(hours) : hours;
    
    if (isNaN(hoursNum)) return '—';
    
    const wholeHours = Math.floor(hoursNum);
    const minutes = Math.round((hoursNum - wholeHours) * 60);
    
    if (minutes === 0) {
      return `${wholeHours}h`;
    } else if (wholeHours === 0) {
      return `${minutes}min`;
    } else {
      return `${wholeHours}h${minutes}min`;
    }
  };

  // Calculate elapsed time (subtracting blocked and paused time)
  const calculateElapsedTime = (startDate, endDate, totalBlockedTime = 0, totalPausedTime = 0) => {
    if (!startDate) return null; // Empty if startDate is null
    
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date(); // Use now if endDate is null
    
    // Calculate total elapsed time in minutes
    const diffMinutes = Math.floor((end - start) / (1000 * 60));
    
    // Subtract blocked and paused time (both are in minutes)
    const effectiveMinutes = Math.max(0, diffMinutes - (totalBlockedTime || 0) - (totalPausedTime || 0));
    
    const hours = Math.floor(effectiveMinutes / 60);
    const minutes = effectiveMinutes % 60;
    
    if (hours === 0 && minutes === 0) return '0min';
    if (hours === 0) return `${minutes}min`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h${minutes}min`;
  };

  // Helper function to transform API task data to component format
  const transformTask = (task) => {
    // Calculate elapsed time (subtracting blocked and paused time)
    const elapsedTime = calculateElapsedTime(
      task.startDate, 
      task.endDate, 
      task.totalBlockedTime, 
      task.totalPausedTime
    );

    // Format estimated duration (in decimal hours)
    const estimatedTime = formatDuration(task.estimatedDuration);

    return {
      id: task.id?.toString(),
      taskNumber: task.taskNumber || `T-${task.id}`,
      // Use assignedToId as technicianId for matching (keep as number for consistent comparison)
      technicianId: task.assignedToId,
      assignedToId: task.assignedToId,
      assignedToUsername: task.assignedToUsername,
      description: task.description || 'Tâche',
      estimatedTime: estimatedTime,
      elapsedTime: elapsedTime,
      status: task.status || 'TODO',
      blockedReason: task.blockedReason || null,
      ...task, // Keep all original task data
    };
  };

  // Fetch vehicle details for a single vehicle
  const fetchVehicle = useCallback(async (vehicleId) => {
    if (!vehicleId) return null;
    
    try {
      const response = await secureGet(`/vehicles/${vehicleId}`, showSnackbar);
      return response.data;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(`Error fetching vehicle ${vehicleId}:`, error);
      }
      return null;
    }
  }, [showSnackbar]);

  // Fetch vehicles for all tasks
  const fetchVehiclesForTasks = useCallback(async (tasksData) => {
    // Get unique vehicle IDs from tasks
    const vehicleIds = [...new Set(tasksData
      .map(task => task.vehicleId)
      .filter(id => id != null && id !== undefined)
    )];

    if (vehicleIds.length === 0) return;

    // Fetch all vehicles in parallel (we'll check if we already have them in the state update)
    const vehiclePromises = vehicleIds.map(id => fetchVehicle(id));
    const vehicleResults = await Promise.all(vehiclePromises);
    
    // Update vehicles state with new data (using functional update to avoid stale closure)
    setVehicles(prevVehicles => {
      const newVehicles = { ...prevVehicles };
      vehicleIds.forEach((id, index) => {
        if (vehicleResults[index] && !newVehicles[id]) {
          newVehicles[id] = vehicleResults[index];
        }
      });
      return newVehicles;
    });
  }, [fetchVehicle]);

  // Helper function to refresh tasks from API
  const refreshTasks = async (familyId = null) => {
    try {
      let response;
      // If a family is selected, use the family-specific endpoint
      const familyIdToUse = familyId || selectedRole;
      if (familyIdToUse) {
        const selectedFamily = families.find(f => f.id === familyIdToUse || f.id === parseInt(familyIdToUse, 10));
        if (selectedFamily) {
          response = await secureGet(`/projects/tasks/by-family/${selectedFamily.id}`, showSnackbar);
          console.log("tasks by family", response.data);
        } else {
          // Fallback to dashboard if family not found
          response = await secureGet('/projects/dashboard', showSnackbar);
          console.log("dashboard (fallback)", response.data);
        }
      } else {
        // No family selected, use dashboard endpoint
        response = await secureGet('/projects/dashboard', showSnackbar);
        console.log("dashboard", response.data);
      }
      
      if (response?.data) {
        const tasksData = (response.data || []).map(task => transformTask(task));
        setTasks(tasksData);

        // Fetch vehicles for all tasks
        await fetchVehiclesForTasks(tasksData);

        // Update task counts for technicians
        setTechnicians(prevTechs =>
          prevTechs.map(tech => {
            const techId = typeof tech.id === 'number' ? tech.id : parseInt(tech.id, 10);
            return {
              ...tech,
              tasksCount: tasksData.filter(t => t.assignedToId === techId || t.assignedToId === tech.id).length,
            };
          })
        );
      }
    } catch (error) {
      console.error('Error refreshing tasks:', error);
      throw error;
    }
  };

  const handleMaintenanceClick = () => {
    setShowMaintenancePopup(true);
  };

  const handleMaintenanceConfirm = async (code) => {
    setShowMaintenancePopup(false);
      try {
      await fetchTechnicians();
    } catch (error) {
      console.error('Error refreshing tasks after maintenance activation:', error);
    }
  };

  const handleMaintenanceCancel = () => {
    setShowMaintenancePopup(false);
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
  };

  const handleTaskDetailClose = () => {
    setShowTaskDetail(false);
    setSelectedTask(null);
  };

  const handleTaskStatusUpdateSuccess = async () => {
    try {
      await refreshTasks();
      showSnackbar('Tâche mise à jour avec succès', 'success');
    } catch (error) {
      console.error('Error refreshing tasks:', error);
      showSnackbar('Erreur lors du rafraîchissement des tâches', 'error');
    }
  };

  // Fetch families
  useEffect(() => {
    const fetchFamilies = async () => {
      try {
        const response = await secureGet('/families/type/SERVICE', showSnackbar);
        console.log("families kanban", response.data);
        
        // Transform API response to match component structure
        const transformedFamilies = (response.data || []).map((family, index) => ({
          id: family.id,
          key: family.id, // Use id as key for consistency
          label: family.name || 'Famille',
          color: TECHNICIAN_COLORS[index % TECHNICIAN_COLORS.length], // Reuse colors
          ...family, // Keep original data
        }));
        
        setFamilies(transformedFamilies);
      } catch (error) {
        console.error('Error fetching families:', error);
        showSnackbar('Erreur lors du chargement des familles', 'error');
        setFamilies([]);
      }
    };

    fetchFamilies();
  }, []);

  // Fetch technicians
  const fetchTechnicians = useCallback(async () => {
    try {
      setLoading(true);
      const response = await secureGet('/users/ROLE_TECHNICIAN', showSnackbar);
      console.log("technicians kanban", response.data);
      
      // Transform API response to match component structure
      const transformedTechnicians = (response.data || []).map((tech, index) => ({
        id: tech.id || tech.username || `T${index + 1}`,
        name: tech.name || tech.username || 'Technicien',
        role: tech.role, // Use tech.role from API, don't use selectedRole here
        tasksCount: 0, // Will be calculated from tasks
        color: TECHNICIAN_COLORS[index % TECHNICIAN_COLORS.length],
        entretien: tech.maintainingPost || false,
        ...tech, // Keep original data
      }));
      
      setTechnicians(transformedTechnicians);
    } catch (error) {
      console.error('Error fetching technicians:', error);
      showSnackbar('Erreur lors du chargement des techniciens', 'error');
      setTechnicians([]);
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array - function is stable

  useEffect(() => {
    fetchTechnicians();
  }, []); // Only run once on mount

  // Fetch tasks from dashboard or by family
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        await refreshTasks();
      } catch (error) {
        console.error('Error fetching tasks:', error);
        showSnackbar('Erreur lors du chargement des tâches', 'error');
        setTasks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [selectedRole, families]);

  // Reset countdown when filters change
  useEffect(() => {
    setReloadCountdown(120); // Reset to 2 minutes when filters change
  }, [selectedRole, families]);

  // Auto-reload tasks every 2 minutes with countdown timer
  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setReloadCountdown((prev) => {
        if (prev <= 1) {
          // Reload tasks when countdown reaches 0 using latest filter values
          const reloadTasks = async () => {
            try {
              let response;
              const familyIdToUse = selectedRoleRef.current;
              if (familyIdToUse) {
                const selectedFamily = familiesRef.current.find(f => f.id === familyIdToUse || f.id === parseInt(familyIdToUse, 10));
                if (selectedFamily) {
                  response = await secureGet(`/projects/tasks/by-family/${selectedFamily.id}`, showSnackbar);
                } else {
                  response = await secureGet('/projects/dashboard', showSnackbar);
                }
              } else {
                response = await secureGet('/projects/dashboard', showSnackbar);
              }
              
              if (response?.data) {
                const tasksData = (response.data || []).map(task => transformTask(task));
                setTasks(tasksData);
                
                // Fetch vehicles for all tasks
                fetchVehiclesForTasks(tasksData);
                
                setTechnicians(prevTechs =>
                  prevTechs.map(tech => {
                    const techId = typeof tech.id === 'number' ? tech.id : parseInt(tech.id, 10);
                    return {
                      ...tech,
                      tasksCount: tasksData.filter(t => t.assignedToId === techId || t.assignedToId === tech.id).length,
                    };
                  })
                );
              }
            } catch (error) {
              console.error('Error auto-reloading tasks:', error);
            }
          };
          reloadTasks();
          return 120; // Reset to 2 minutes
        }
        return prev - 1;
      });
    }, 1000); // Update every second

    return () => {
      clearInterval(countdownInterval);
    };
  }, []); // Empty dependency array - interval runs independently

  // Filter technicians by selected family role (if a family is selected)
  const filteredTechnicians = selectedRole 
    ? technicians.filter(tech => tech.role === selectedRole)
    : technicians; // Show all technicians when no family is selected
  const totalTechnicians = filteredTechnicians.length;

  // Filter tasks: when no technician selected, show all tasks (not filtered by role)
  // When a technician is selected, show only that technician's tasks
  const filteredTasks = selectedTechnician
    ? tasks.filter(task => {
        // Match by assignedToId with selectedTechnician (handle both number and string)
        const selectedId = typeof selectedTechnician === 'number' ? selectedTechnician : parseInt(selectedTechnician, 10);
        return task.assignedToId === selectedId || task.assignedToId === selectedTechnician;
      })
    : tasks; // Show all tasks when "TOUS LES TECHNICIENS" is selected

  const getTasksByStatus = (status) => {
    return filteredTasks.filter(task => task.status === status);
  };

  const getTechnicianById = (id) => {
    // Handle both number and string IDs for matching
    if (typeof id === 'number') {
      return technicians.find(tech => {
        const techId = typeof tech.id === 'number' ? tech.id : parseInt(tech.id, 10);
        return techId === id;
      });
    }
    return technicians.find(tech => {
      const techId = typeof tech.id === 'number' ? tech.id : parseInt(tech.id, 10);
      const searchId = parseInt(id, 10);
      return techId === searchId || tech.id === id;
    });
  };

  return (
    <div className="min-h-screen bg-[#F8F8F8]">
      <Helmet>
        <title>GrayFox - Opérations atelier</title>
      </Helmet>

      {/* Top Header */}
      <div className="relative">
        <div style={blurStyle}></div>
        <div className="px-8 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <img src={logoGF} alt="Logo" className="h-8" />
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            <LoggedUserCard />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-8 py-6">
        {/* Page Title */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-2">
            <h1 className="text-[40px] font-semibold text-[#38485C]">
              Opérations atelier Ain sbaa
            </h1>
          </div>
          {/* Warning Banner with Countdown */}
          <div className="px-3 py-2 bg-[#FFF6CE] rounded-[12px] flex items-center gap-2">
            <img src={LoaderIcon} alt="Loader" className="w-4 h-4" />
            <span className="text-[16px] font-semibold text-[#4D5B68]">
              {t('kanban.reload_in') || 'Rechargement dans'}: <span className="font-extrabold">{Math.floor(reloadCountdown / 60)}:{(reloadCountdown % 60).toString().padStart(2, '0')}</span> {t('kanban.reload_warning') || 'Veuillez vous assurer de ne pas mettre à jour le statut d\'une tâche à la fin des 2 minutes car la page se rechargera et les données pourraient ne pas être envoyées'}
            </span>
          </div>
        </div>

        {/* Families Tabs */}
        {families.length > 0 && (
          <div className="flex bg-gray-200 rounded-[12px] p-1 mb-4 gap-1">
            {/* Deselect All Button */}
            <button
              onClick={() => {
                setSelectedRole(null);
                setSelectedTechnician(null);
              }}
              className={`px-4 py-3 text-[14px] rounded-[12px] whitespace-nowrap ${
                selectedRole === null
                  ? 'bg-white text-primary font-bold'
                  : 'text-[#4D5B68] font-semibold'
              }`}
            >
              {t('kanban.all_services') || 'Tous'}
            </button>
            
            {families.map((family) => (
              <button
                key={family.id}
                onClick={() => {
                  // Toggle: if already selected, deselect it; otherwise select it
                  if (selectedRole === family.id) {
                    setSelectedRole(null);
                  } else {
                    setSelectedRole(family.id);
                  }
                  setSelectedTechnician(null);
                }}
                className={`flex-1 py-3 text-[14px] rounded-[12px] ${
                  selectedRole === family.id
                    ? 'bg-white text-primary font-bold'
                    : 'text-[#4D5B68] font-semibold'
                }`}
              >
                {family.label}
              </button>
            ))}
          </div>
        )}

        {/* Technician Filters */}
        <div className="mb-6">
          <p className="text-[12px] font-semibold text-[#707070] mb-1">FILTRER PAR TECHNICIEN</p>
          <div className="flex items-center gap-3 flex-wrap">
            {/* All Technicians */}
            <button
              onClick={() => setSelectedTechnician(null)}
              className={`px-3 py-1 rounded-[12px] text-[14px] font-semibold transition-colors shadow-[0px_6px_26px_#626B7D12] ${
                selectedTechnician === null
                  ? 'bg-primary text-white '
                  : 'bg-white text-[#38485C] border border-gray-200'
              }`}
            >
              TOUS LES TECHNICIENS ({filteredTasks.length})
            </button>

            {/* Individual Technicians */}
            {technicians.map((tech) => (
              <button
                key={tech.id}
                onClick={() => setSelectedTechnician(tech.id)}
                className={`px-3 py-1 rounded-[12px] text-[14px] font-semibold transition-colors shadow-[0px_6px_26px_#626B7D12] flex items-center gap-2`}
                style={{
                  backgroundColor: selectedTechnician === tech.id ? tech.color : 'white',
                  color: selectedTechnician === tech.id ? 'white' : '#38485C',
                }}
              >
                {tech.entretien && (
                  <img 
                    src={wrenchIcon} 
                    alt="maintenance" 
                    className="w-4 h-4"
                    style={{
                      filter: selectedTechnician === tech.id 
                        ? 'brightness(0) invert(1)' // White when selected
                        : 'brightness(0) saturate(100%) invert(8%) sepia(12%) saturate(3000%) hue-rotate(200deg) brightness(0.15) contrast(1.4)' // #16283E when not selected
                    }}
                  />
                )}
                <span>{tech.workshopId} • {tech.name} ({tech.tasksCount})</span>
              </button>
            ))}

            {/* Maintenance Button */}
            <button
              onClick={handleMaintenanceClick}
              className="ml-auto px-4 py-2 bg-white text-primary font-bold text-[14px] rounded-[12px] flex items-center gap-2 hover:bg-primary/5"
            >
              <img src={wrenchIcon} alt="wrench" className="w-4 h-4" />
              Entretien de poste
            </button>
          </div>
        </div>

        {/* Kanban Board */}
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <LoadingLogo />
          </div>
        ) : (
          <div className="grid grid-cols-5 gap-4 h-full">
            {STATUSES.map((status) => {
              const statusTasks = getTasksByStatus(status.key);
              return (
                <div key={status.key} className="bg-[#E9EAEE] rounded-[12px] flex flex-col h-full min-h-[600px]">
                  {/* Column Header */}
                  <div className="px-4 py-3">
                    <h3 className="text-[16px] font-bold text-primary flex items-center gap-2">
                      <span className="w-2 h-2 bg-primary rounded-full"></span>
                      {status.label} ({statusTasks.length})
                    </h3>
                  </div>

                  {/* Task Cards Container */}
                  <div className="flex-1 px-2 space-y-3 h-auto">
                    {statusTasks.map((task) => {
                      // Get technician by assignedToId
                      const technician = getTechnicianById(task.assignedToId);
                      
                      // Use formatted estimatedTime and elapsedTime from transformTask
                      const estimatedTime = task.estimatedTime || formatDuration(task.estimatedDuration);
                      const elapsedTime = task.elapsedTime || calculateElapsedTime(
                        task.startDate, 
                        task.endDate, 
                        task.totalBlockedTime, 
                        task.totalPausedTime
                      );

                      // Check if overtime (compare elapsed vs estimated, accounting for blocked/paused time)
                      let overtimeCheck = false;
                      if (elapsedTime && task.estimatedDuration && task.startDate) {
                        const start = new Date(task.startDate);
                        const end = task.endDate ? new Date(task.endDate) : new Date();
                        const totalElapsedMinutes = Math.floor((end - start) / (1000 * 60));
                        // Subtract blocked and paused time (both are in minutes)
                        const effectiveMinutes = Math.max(0, totalElapsedMinutes - (task.totalBlockedTime || 0) - (task.totalPausedTime || 0));
                        const estimatedMinutes = Math.round((task.estimatedDuration || 0) * 60);
                        overtimeCheck = effectiveMinutes > estimatedMinutes;
                      }

                      return (
                        <div
                          key={task.id}
                          onClick={() => handleTaskClick(task)}
                          className="bg-white rounded-[12px] shadow-[0px_6px_26px_#626B7D12] cursor-pointer relative overflow-hidden hover:shadow-[0px_8px_30px_#626B7D20] transition-shadow"
                        >
                          {/* Left Colored Border */}
                          <div
                            className="absolute left-0 top-[10%] w-[10px] h-[80%] flex items-center justify-center rounded-r-[12px]"
                            style={{ backgroundColor: technician?.color || '#ACAFC6' }}
                          />

                          <div className="pl-6 pr-4 py-4">
                            {/* Technician Badge */}
                            <div
                              className="inline-block px-4 py-2 rounded-[12px] text-[12px] font-bold text-white mb-2 text-center"
                              style={{ backgroundColor: technician?.color || '#ACAFC6' }}
                            >
                              {technician?.name || task.assignedToUsername || 'Non assigné'}
                            </div>

                            {/* Vehicle Info */}
                            {task.vehicleId && vehicles[task.vehicleId] && (
                              <div className="mb-1">
                                <p className="text-[12px] font-semibold text-[#707070]">
                                  {[
                                    vehicles[task.vehicleId].brand?.name,
                                    vehicles[task.vehicleId].model,
                                    vehicles[task.vehicleId].color
                                  ].filter(Boolean).join(' • ')}
                                </p>
                              </div>
                            )}

                            {/* Task Title */}
                            <p className="text-[16px] font-semibold text-[#16283E] mb-2">
                              {task.taskNumber || `T-${task.id}`} • {task.description || 'Tâche'}
                            </p>

                            {/* Time Info */}
                            <div className="flex items-center justify-between text-[12px]">
                              <p className="text-[#707070] font-semibold ">
                                ESTIMÉ: <span className="font-bold text-[#3872B0]">{estimatedTime}</span>
                              </p>
                              <p className={`font-semibold text-[#707070]`}>
                                ÉCOULÉ: <span className={`font-bold ${overtimeCheck ? 'text-[#EF466F]' : 'text-[#707070]'}`}>{elapsedTime || '—'}</span>
                              </p>
                            </div>

                            {/* Block Reason */}
                            {task.blockedReason && (
                              <p className="text-[12px] text-[#EF466F] font-bold mt-2">
                                BLOQUÉ: {t(`blocked_reasons.${task.blockedReason}`)}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <MaintenanceAuthPopup
        open={showMaintenancePopup}
        onConfirm={handleMaintenanceConfirm}
        onCancel={handleMaintenanceCancel}
        onSuccess={() => showSnackbar('Mode entretien modifié', 'success')}
        onError={(message) => showSnackbar(message || 'Erreur lors de la modification du mode entretien', 'error')}
      />

      <TaskDetailPopup
        open={showTaskDetail}
        task={selectedTask}
        technician={selectedTask ? getTechnicianById(selectedTask.assignedToId) : null}
        onClose={handleTaskDetailClose}
        onSuccess={handleTaskStatusUpdateSuccess}
      />

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default Kanban;

